import { useState, useEffect } from "react";
import { getBoardsConfigurePanelValues } from "../configure-panel";
import {
   BOARDS_CONFIGURE_PANEL_UPDATE_EVENT,
   BOARDS_CONFIGURE_PANEL_KEY,
   maybeUpdateColumnWidths,
} from "../utils";

/**
 * Hook to track the columnWidth value from the boards-configure-panel localStorage.
 * This allows components to react to changes in column width selection.
 *
 * As a side-effect, applies the CSS custom property for column widths whenever
 * the value changes, consolidating the call that was previously spread across
 * multiple locations (updateFilter, clearAllFilters, and a standalone useEffect).
 *
 * @returns {string} The current columnWidth value (small, medium, or large)
 */
export function useColumnWidthTracker(): string {
   const [columnWidth, setColumnWidth] = useState<string>(() => {
      return getBoardsConfigurePanelValues().columnWidth;
   });

   useEffect(() => {
      const handleStorageChange = (event: CustomEvent) => {
         if (event.detail.key === BOARDS_CONFIGURE_PANEL_KEY) {
            try {
               const parsedValue = JSON.parse(event.detail.value);
               if (parsedValue.columnWidth) {
                  setColumnWidth(parsedValue.columnWidth);
               }
            } catch (error) {
               console.warn("Failed to parse boards-configure-panel localStorage value:", error);
            }
         }
      };

      // Listen for the custom event dispatched by updateBoardsConfigurePanelLocalStorage
      window.addEventListener(
         BOARDS_CONFIGURE_PANEL_UPDATE_EVENT,
         handleStorageChange as EventListener,
      );

      return () => {
         window.removeEventListener(
            BOARDS_CONFIGURE_PANEL_UPDATE_EVENT,
            handleStorageChange as EventListener,
         );
      };
   }, []);

   useEffect(() => {
      maybeUpdateColumnWidths();
   }, [columnWidth]);

   return columnWidth;
}
